import { useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Card } from '../../components/Card';
import { StatusBadge } from '../../components/StatusBadge';
import type { DirectoryUserDetailResponse, DirectoryUserWorkspace } from '../../types/api';
import { useAdminResource } from './useAdminResource';
import { DirectoryError, FactList, backTarget } from './parts';
import { APPS_UNAVAILABLE_TEXT, formatDate, formatDateTime, humanize, yesNo } from './labels';
import { CardsSkeleton, PageHeader, secondaryButton } from '../../components/admin/AdminUI';
import { UserActions, UserStatusBadges } from './UserManagement';
import { AddToWorkspaceDialog, MemberActions } from './WorkspaceManagement';

interface ActivityResponse {
  activity: Array<{ id: string; action: string; actorEmail: string | null; createdAt: string; outcome: string | null }>;
}

const ACTIVITY_TEXT: Record<string, string> = {
  'admin.user.created': 'Account created',
  'admin.user.updated': 'Name or email edited',
  'admin.user.email_verified': 'Email marked verified',
  'admin.user.deactivated': 'Deactivated',
  'admin.user.reactivated': 'Reactivated',
  'admin.user.password.temporary_set': 'Temporary password set',
  'admin.user.password.reset_link_issued': 'Reset link sent',
  'admin.user.password.reset_link_email_failed': 'Reset email could not be queued',
  'admin.user.central_admin.granted': 'Made Central admin',
  'admin.user.central_admin.revoked': 'Central admin removed',
  'admin.workspace.member.added': 'Added to a workspace',
  'admin.workspace.member.role_changed': 'Workspace role changed',
  'admin.workspace.member.removed': 'Removed from a workspace',
  'admin.workspace.member.app_roles_changed': 'App access changed',
  'user.password.changed': 'Changed their password',
};

/** Admin actions recorded against this person, newest first (the audit log). */
function ActivityCard({ userId, version }: { userId: string; version: number }) {
  const { data, error } = useAdminResource<ActivityResponse>(`/users/${encodeURIComponent(userId)}/activity?v=${version}`);
  return (
    <Card title="Admin activity" description="Changes made to this account, from the audit log.">
      {error ? (
        <p className="text-sm text-slate-500">Could not load activity.</p>
      ) : !data ? (
        <p role="status" className="text-sm text-slate-500">Loading…</p>
      ) : data.activity.length === 0 ? (
        <p className="text-sm text-slate-500">No admin changes yet.</p>
      ) : (
        <ul className="divide-y divide-slate-100" data-testid="user-activity">
          {data.activity.map((a) => (
            <li key={a.id} className="flex flex-wrap items-baseline justify-between gap-2 py-2 text-sm">
              <span className="text-slate-800">
                {ACTIVITY_TEXT[a.action.replace(/\.rejected$/, '')] ?? a.action}
                {a.outcome === 'rejected' && <span className="ml-2 text-xs text-red-600">(refused)</span>}
              </span>
              <span className="text-xs text-slate-500">
                {a.actorEmail ? `by ${a.actorEmail} · ` : ''}
                {formatDateTime(a.createdAt)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function AppsCell({ row }: { row: DirectoryUserWorkspace }) {
  if (row.apps === null) {
    return (
      <span className="text-slate-500">
        {APPS_UNAVAILABLE_TEXT[row.appsUnavailableReason ?? 'lookup_failed']}
      </span>
    );
  }
  if (row.apps.length === 0) return <span className="text-slate-500">No apps</span>;
  return (
    <div className="flex flex-wrap gap-1">
      {row.apps.map((app) => (
        <StatusBadge key={app.appId} status="neutral" label={app.displayName} />
      ))}
    </div>
  );
}

/**
 * Admin console: one person — profile flags, every workspace they belong to
 * with their role there, and the apps that workspace shows them. Read-only.
 */
export function UserDetailPage() {
  const { userId = '' } = useParams();
  const location = useLocation();
  const { data, error, loading, reload } = useAdminResource<DirectoryUserDetailResponse>(
    `/users/${encodeURIComponent(userId)}`,
  );
  const back = backTarget(location.state, '/users');
  const [version, setVersion] = useState(0);
  const [adding, setAdding] = useState(false);
  const changed = () => {
    reload();
    setVersion((n) => n + 1);
  };

  return (
    <div className="space-y-6">
      <Link to={back} className="inline-flex text-sm font-medium text-slate-500 hover:text-emerald-700">
        ← All users
      </Link>

      {loading ? (
        <CardsSkeleton message="Loading this user…" />
      ) : error ? (
        <DirectoryError error={error} what="This user" onRetry={reload} />
      ) : data ? (
        <>
          <PageHeader
            breadcrumb={[{ label: 'Users', to: back }, { label: 'User' }]}
            title={data.user.name || data.user.email}
            subtitle={data.user.email}
            actions={<UserActions user={data.user} onChanged={changed} menuLabel="Actions for this person" />}
          />

          <Card title="Profile">
            <FactList
              items={[
                { label: 'Status', value: <UserStatusBadges user={data.user} /> },
                {
                  label: 'Signed in now',
                  value: (data.user.activeSessions ?? 0) > 0 ? `Yes (${data.user.activeSessions} session${data.user.activeSessions === 1 ? '' : 's'})` : 'No',
                },
                ...(data.user.deactivatedAt ? [{ label: 'Deactivated', value: formatDateTime(data.user.deactivatedAt) }] : []),
                { label: 'Email', value: data.user.email },
                { label: 'Name', value: data.user.name || '—' },
                { label: 'User ID', value: <span className="font-mono text-xs">{data.user.id}</span> },
                { label: 'Email verified', value: yesNo(data.user.isEmailVerified) },
                { label: 'Central admin', value: yesNo(data.user.isCentralAdmin) },
                { label: 'Platform internal', value: yesNo(data.user.isPlatformInternal) },
                { label: 'Joined', value: formatDate(data.user.createdAt) },
                {
                  label: 'Last sign-in',
                  value: data.user.lastSignInAt ? formatDateTime(data.user.lastSignInAt) : 'Never',
                },
              ]}
            />
          </Card>

          <Card
            title={`Workspaces (${data.workspaces.length})`}
            actions={
              <button
                type="button"
                className={secondaryButton}
                onClick={() => setAdding(true)}
                disabled={!!data.user.deactivatedAt}
                title={data.user.deactivatedAt ? 'Reactivate the account first' : undefined}
              >
                Add to workspace
              </button>
            }
          >
            <AddToWorkspaceDialog
              open={adding}
              userEmail={data.user.email}
              onClose={() => setAdding(false)}
              onDone={() => {
                setAdding(false);
                changed();
              }}
            />
            {data.workspaces.length === 0 ? (
              <p className="text-sm text-slate-500">Not a member of any workspace.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full border-separate border-spacing-0 text-sm">
                  <thead>
                    <tr>
                      {['Workspace', 'Type', 'Role', 'Membership', 'Billing', 'Joined', 'Apps they see', ''].map((h) => (
                        <th
                          key={h}
                          className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap"
                        >
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white">
                    {data.workspaces.map((row) => (
                      <tr key={row.workspace.id} data-testid="user-workspace-row" className="group transition-colors even:bg-slate-50/40 hover:bg-emerald-50/40">
                        <td className="border-b border-slate-100 px-4 py-3.5">
                          <Link
                            to={`/workspaces/${row.workspace.id}`}
                            className="font-medium text-emerald-700 hover:text-emerald-800 hover:underline"
                          >
                            {row.workspace.name}
                          </Link>
                          {row.workspace.status !== 'active' && (
                            <span className="ml-2">
                              <StatusBadge status="warning" label={humanize(row.workspace.status)} />
                            </span>
                          )}
                        </td>
                        <td className="border-b border-slate-100 px-4 py-3.5">{humanize(row.workspace.type)}</td>
                        <td className="border-b border-slate-100 px-4 py-3.5">{humanize(row.membership.role)}</td>
                        <td className="border-b border-slate-100 px-4 py-3.5">
                          <StatusBadge
                            status={row.membership.status === 'active' ? 'success' : 'warning'}
                            label={humanize(row.membership.status)}
                          />
                        </td>
                        <td className="border-b border-slate-100 px-4 py-3.5">{humanize(row.membership.billingClass)}</td>
                        <td className="border-b border-slate-100 px-4 py-3.5">{formatDate(row.membership.joinedAt)}</td>
                        <td className="border-b border-slate-100 px-4 py-3.5 max-w-md">
                          <AppsCell row={row} />
                        </td>
                        <td className="border-b border-slate-100 px-4 py-3.5 text-right">
                          <MemberActions
                            workspace={row.workspace}
                            member={{
                              userId: data.user.id,
                              email: data.user.email,
                              role: row.membership.role,
                              status: row.membership.status,
                            }}
                            apps={null}
                            onChanged={changed}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <ActivityCard userId={data.user.id} version={version} />
        </>
      ) : null}
    </div>
  );
}
